import { toApiError, type CharacterSchemas } from '@ddt/api-client';
import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import { getServices } from '../services';
import { useMatch } from './match';
import { num } from './session';

/** O contrato aceita `number | string` nos inteiros; a tela só usa número. */
export interface Friend {
  characterId: number;
  accountId: string;
  nickName: string;
  grade: number;
  sex: boolean;
  style: string;
}
export interface BlockedPlayer {
  characterId: number;
  nickName: string;
}

const toFriend = (f: CharacterSchemas['FriendResponse']): Friend => ({ ...f, characterId: num(f.characterId), grade: num(f.grade) });
const toBlocked = (b: CharacterSchemas['BlockedResponse']): BlockedPlayer => ({ ...b, characterId: num(b.characterId) });

/** Um amigo com a presença que o Match informou. Sem presença ainda, vale offline. */
export interface FriendView extends Friend {
  online: boolean;
  inGame: boolean;
}

/** Resposta da API: o que falhar vira `ApiError` com o código estável do servidor (`describeError` escreve o texto). */
function ok<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (!result.response.ok) throw toApiError(result.response.status, result.error);
  return result.data as T;
}

/**
 * Lista de amigos e de bloqueados (ADR 0023). A relação vive no Character; a presença (online ou em partida) vem do Match.
 * A amizade é de mão única: adicionar não precisa de aceite, e quem bloqueou some da lista de quem o adicionou.
 */
export const useFriends = defineStore('friends', () => {
  const friends = ref<Friend[]>([]);
  const blocked = ref<BlockedPlayer[]>([]);
  const presence = reactive<Record<string, { online: boolean; inGame: boolean }>>({});
  const loaded = ref(false);

  /** Online primeiro, depois em ordem alfabética. */
  const list = computed<FriendView[]>(() =>
    friends.value
      .map((f) => ({ ...f, online: presence[f.accountId]?.online ?? false, inGame: presence[f.accountId]?.inGame ?? false }))
      .sort((a, b) => Number(b.online) - Number(a.online) || a.nickName.localeCompare(b.nickName, 'pt-BR')),
  );

  /** Baixa as duas listas do Character. */
  async function load(): Promise<void> {
    const { api } = getServices();
    const [f, b] = await Promise.all([api.character.GET('/v1/characters/me/friends', {}), api.character.GET('/v1/characters/me/blocks', {})]);
    friends.value = (ok(f) ?? []).map(toFriend);
    blocked.value = (ok(b) ?? []).map(toBlocked);
    loaded.value = true;
  }

  /** Pergunta ao Match quem está online. O freio do servidor (2 s) recusa pedidos em sequência: quem chama ignora a falha. */
  async function refreshPresence(): Promise<void> {
    const result = await useMatch().friendsPresence();
    const online = new Set<string>();
    for (const p of result) {
      presence[p.accountId] = { online: p.online, inGame: p.inGame };
      online.add(p.accountId);
    }
    for (const id of Object.keys(presence)) if (!online.has(id)) delete presence[id];
  }

  async function add(nickName: string): Promise<void> {
    const { api } = getServices();
    const created = toFriend(ok(await api.character.POST('/v1/characters/me/friends', { body: { nickName } })));
    friends.value = [...friends.value.filter((f) => f.characterId !== created.characterId), created];
  }

  async function remove(characterId: number): Promise<void> {
    const { api } = getServices();
    ok(await api.character.DELETE('/v1/characters/me/friends/{characterId}', { params: { path: { characterId } } }));
    friends.value = friends.value.filter((f) => f.characterId !== characterId);
  }

  /** Bloquear tira o jogador da lista de amigos nos dois sentidos. */
  async function block(nickName: string): Promise<void> {
    const { api } = getServices();
    const created = toBlocked(ok(await api.character.POST('/v1/characters/me/blocks', { body: { nickName } })));
    blocked.value = [...blocked.value.filter((b) => b.characterId !== created.characterId), created];
    friends.value = friends.value.filter((f) => f.characterId !== created.characterId);
  }

  async function unblock(characterId: number): Promise<void> {
    const { api } = getServices();
    ok(await api.character.DELETE('/v1/characters/me/blocks/{characterId}', { params: { path: { characterId } } }));
    blocked.value = blocked.value.filter((b) => b.characterId !== characterId);
  }

  async function invite(accountId: string): Promise<void> {
    await useMatch().inviteFriend(accountId);
  }

  function reset(): void {
    friends.value = [];
    blocked.value = [];
    for (const id of Object.keys(presence)) delete presence[id];
    loaded.value = false;
  }

  return { friends, blocked, list, loaded, load, refreshPresence, add, remove, block, unblock, invite, reset };
});
